import { Application } from '@tencent/alloy-renderer';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { RenderArea } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/thumbnail-mobile/render-area';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { reaction } from 'mobx';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { INewMeetingRoomAppServices } from '@src/slide/application/entry/toc/new-meeting-room/new-meeting-room-app-services.interface';
import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';

interface IRenderLayerServiceProps {
    region: string;
    mobileEditMode: () => any;
    width: number;
    height: number;
    onDidRenderCallback: (slideComponent: ISlideComponent) => void;
}

/**
 * 移动端列表页Service
 * 每页 Slide 对应一棵渲染树，屏幕可视区域内页面的渲染树挂载到 Application 上，
 * 页面移出屏幕时将渲染树从 Application 上卸载，回收 Application，减少 Canvas 画布
 */
export class MobileRenderLayerService {
    private static serviceInstance: MobileRenderLayerService;

    public static getInstance(): MobileRenderLayerService {
        if (!MobileRenderLayerService.serviceInstance) {
            MobileRenderLayerService.serviceInstance = new MobileRenderLayerService();
        }
        return MobileRenderLayerService.serviceInstance;
    }

    private hasInit = false;
    private region: string;
    private mobileEditMode: () => any;
    private renderAreaMap: Map<ISlideModel, RenderArea> = new Map<ISlideModel, RenderArea>();
    private width: number;
    private height: number;
    private scale: number;
    private availableApps = new Set<Application>();
    private needRender: boolean;
    private listChangeListener;
    private unmounted = false;
    private onDidRenderCallback: (slideComponent: ISlideComponent) => void;

    public initService = (props: IRenderLayerServiceProps): void => {
        if (this.hasInit) {
            return;
        }
        this.hasInit = true;
        const {
            region,
            mobileEditMode,
            width,
            height,
            onDidRenderCallback,
        } = props;
        this.region = region;
        this.mobileEditMode = mobileEditMode;
        this.onDidRenderCallback = onDidRenderCallback;
        this.updateInfo(width, height);
        this.buildAllSlidesCache(this.getAppServices().viewableServiceLayer.selectionService.slides);

        this.addSlidesChangeListener();
    };

    /**
     * slide出现在可视区域内渲染
     */
    public handleCellAppear = (): void => {
        const { slideWidth, slideHeight } = this.getAppServices().getUiDataService().presentation.props;
        const scale = this.width / slideWidth < this.height / slideHeight
            ? this.width / slideWidth : this.height / slideHeight;
        if (scale !== this.scale) {
            this.scale = scale;
        }

        this.renderVisibleSlide();
    };

    /**
     * slide移出可视区域时隐藏
     * @param index
     */
    public handleCellDisappear = (index: number): void => {
        const slideModel = this.getAppServices().viewableServiceLayer.selectionService.slides[index];

        this.hidden(slideModel);
    };

    /**
     * slide渲染树内容变更触发重新渲染
     * @param slide
     */
    public handleSlideChange = (slide: ISlideModel): void => {
        const renderArea = this.getRenderArea(slide);

        renderArea.callbacks.forEach((callback) => {
            callback?.(renderArea.slideImageSrc, renderArea.slideComponent);
        });
    };

    public updateInfo = (width: number, height: number): void => {
        // 预览态和编辑态列表页使用同一张图，确保只有预览态更改scale，进入编辑态不更改scale，内容变更时生成预览态尺寸大小图片
        if (this.mobileEditMode()) {
            // 首次进入编辑态，隐藏列表页首页画布
            if (!this.unmounted) {
                this.unmounted = true;
                this.unmountVisibleSlide();
            }
            return;
        }
        const { slideWidth, slideHeight } = this.getAppServices().getUiDataService().presentation.props;
        const scale = width / slideWidth < height / slideHeight ? width / slideWidth : height / slideHeight;
        if ((width === this.width) && (height === this.height) && (scale === this.scale)) {
            return;
        }
        this.width = width;
        this.height = height;
        this.scale = scale;
        this.renderVisibleSlide();
    };

    public destroyLayer(): void {
        this.getAppServices().viewableServiceLayer.animManager.playSlideComponent = undefined;
        this.listChangeListener?.();
    }

    /**
     * 注册生成图片成功回调
     * 内容变更重新生成图片后会调用MobileScaleSlide中方法更新图片url
     * @param slideModel
     * @param callback
     */
    public injectGenerateImageCallback = (slideModel: ISlideModel, callback): void => {
        const renderArea = this.getRenderArea(slideModel);
        renderArea.callbacks.push(callback);
    };

    public removeGenerateImageCallback = (slideModel: ISlideModel, callback): void => {
        const renderArea = this.getRenderArea(slideModel);
        const index = renderArea.callbacks.indexOf(callback);
        if (index !== -1) {
            renderArea.callbacks.splice(index, 1);
        }
    };

    public getRenderArea(slideModel: ISlideModel): RenderArea {
        if (!this.renderAreaMap.has(slideModel)) {
            return this.buildSingleSlideCache(slideModel);
        }
        return this.renderAreaMap.get(slideModel) as RenderArea;
    }

    public getSlideImageSrc(slideModel: ISlideModel): string {
        const renderArea = this.getRenderArea(slideModel);
        return renderArea.slideImageSrc;
    }

    /**
     * 绘制可视区域内页面
     */
    private renderVisibleSlide = () => {
        this.getAppServices().viewableServiceLayer.slideRenderStateManager.getVisibleSlideKeyList()
            .forEach((cellKey) => {
                const slideIndex = this.getAppServices().viewableServiceLayer.selectionService.slides
                    .findIndex(slide => slide.key === cellKey);
                this.display(this.getAppServices().viewableServiceLayer.selectionService
                    .slides[slideIndex]);
            });
    };

    private unmountVisibleSlide = () => {
        this.getAppServices().viewableServiceLayer.slideRenderStateManager.getVisibleSlideKeyList()
            .forEach((cellKey) => {
                const slideIndex = this.getAppServices().viewableServiceLayer.selectionService.slides
                    .findIndex(slide => slide.key === cellKey);
                const renderArea = this.getRenderArea(this.getAppServices().viewableServiceLayer.selectionService
                    .slides[slideIndex]);
                if (renderArea.canvasWrapper) {
                    renderArea.unmount();
                }
            });
    };

    /**
     * 页面显示时挂载渲染树，并按需渲染
     * @param slideModel
     */
    private display = (slideModel) => {
        if (!slideModel) {
            return;
        }
        const renderArea = this.getRenderArea(slideModel);
        renderArea.mount();

        if (!renderArea.application) {
            renderArea.application = this.getTempApplication();
            // addChild 在 autoRender 为 true 的情况下会触发渲染，先设置为 false，addChild 操作结束后再设置 true
            renderArea.application.autoRender = false;
            renderArea.application.stage.addChild(renderArea);
            renderArea.application.autoRender = true;

            if (renderArea.needRender()) {
                renderArea.application.requestRender();
            }
        } else {
            this.updateTempApplication(renderArea.application);
            if (this.needRender) {
                renderArea.application.requestRender();
                this.needRender = false;
            }
        }
    };

    /**
     * 页面隐藏时卸载渲染树，并回收Application
     * @param slideModel
     */
    private hidden = (slideModel) => {
        const renderArea = this.renderAreaMap.get(slideModel);
        if (renderArea?.application) {
            // 避免首页未生成图片时就隐藏掉canvas出现空白的场景
            const slideIndex = slideModel.parent.children.findIndex(e => e === slideModel);
            if (slideIndex === 0 && !renderArea.slideImageSrc) {
                return;
            }

            if (renderArea.canvasWrapper) {
                // cell-render的canvas画布在iframe中，在3.0安卓本地偶现渲染失败的问题，暂时没有定位到根因，先不作为可切换的
                const canvasDom = renderArea.application?.view?.native;
                if (canvasDom?.style.display === 'block') {
                    canvasDom.style.display = 'none';
                }
                return;
            }
            renderArea.application.autoRender = false;
            renderArea.application.stage.removeChild(renderArea);
            renderArea.application.autoRender = true;
            renderArea.unmount();
            this.availableApps.add(renderArea.application);
            renderArea.application = null;
        }
    };

    private buildAllSlidesCache(slideList: ISlideModel[]) {
        for (let i = 0; i < slideList.length; i++) {
            const slide = slideList[i];
            // 移动端列表页前三页同步构建
            if (i < 3) {
                if (!this.renderAreaMap.has(slide)) {
                    this.buildSingleSlideCache(slide);
                }
            } else {
                TaskManager.addTaskWithBlock(`slide-list-${slide.key}`, TaskPriority.LOW, () => {
                    if (!this.renderAreaMap.has(slide)) {
                        this.buildSingleSlideCache(slide);
                    }
                });
            }
        }
    }

    private buildSingleSlideCache(slide: ISlideModel): RenderArea {
        const renderArea = new RenderArea(
            slide,
            () => this.scale,
            this.region,
            false,
            this.mobileEditMode,
            this.handleSlideChange,
            this.onDidRenderCallback,
        );
        this.renderAreaMap.set(slide, renderArea);
        return renderArea;
    }

    private addSlidesChangeListener() {
        this.listChangeListener = reaction(() => [
            this.getAppServices().viewableServiceLayer.selectionService.slides,
        ], () => {
            this.handleSlideListChange();
        });
    }

    private handleSlideListChange() {
        const { slides } = this.getAppServices().viewableServiceLayer.selectionService;
        const newMap = new Map<ISlideModel, boolean>();
        slides.forEach((slide: ISlideModel) => {
            newMap.set(slide, true);
        });
        this.renderAreaMap.forEach((renderArea, slideModel) => {
            if (!newMap.has(slideModel)) {
                this.hidden(slideModel);
                const renderArea = this.renderAreaMap.get(slideModel);
                // 触发所有子孙节点dispose生命周期
                renderArea?.dispose?.();
                this.renderAreaMap.delete(slideModel);
            }
        });
        this.buildAllSlidesCache(slides);
    }

    private getTempApplication = (): Application => {
        let application;
        if (!this.availableApps.size) {
            application =  this.createTempApplication();
        } else {
            application = this.availableApps.values().next().value;
            this.updateTempApplication(application);
            this.availableApps.delete(application);
        }
        return application;
    };

    private createTempApplication(): Application {
        const canvasDom = document.createElement('canvas');
        canvasDom.style.position = 'absolute';
        canvasDom.style.left = '0';
        canvasDom.style.top = '0';
        canvasDom.style.display = 'none';

        const target = new BrowserTarget();
        const application = new Application(target, {
            interactive: false,
            native: canvasDom,
            delayDrawHit: false,
        });
        application.resolution = Math.ceil(window.devicePixelRatio);
        document.body.appendChild(canvasDom);
        this.updateTempApplication(application);
        return application;
    }

    private updateTempApplication(application: Application): void {
        const width = Math.round(this.getAppServices().getUiDataService().presentation.props.slideWidth * this.scale);
        const height = Math.round(this.getAppServices().getUiDataService().presentation.props.slideHeight * this.scale);
        const { devicePixelRatio, mobileZoomScale = 1 } = window;
        const resolution = devicePixelRatio * mobileZoomScale;
        if (
            application.width === width
            && application.height === height
            && application.resolution ===  Math.ceil(resolution)
        ) {
            return;
        }
        application.resolution = Math.ceil(resolution);
        application.width = width;
        application.height = height;
        application.stage.transform.scaleX = this.scale;
        application.stage.transform.scaleY = this.scale;
        this.needRender = true;
    }

    private getAppServices() {
        return AppServiceProvider.getInstance().getAppServices() as
            (IMobileEditorAppServices | INewMeetingRoomAppServices);
    }
}
